import { PencilIcon } from '@heroicons/react/24/solid';
import React, { useRef } from 'react';

import { motion } from 'framer-motion';
import { AppImage } from 'src/shared/components';
import { ProductById } from 'src/shared/types/product';

import {
  addToast,
  HeroModal as Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  useDisclosure
} from 'src/shared/components/ui';
import { ButtonV2 } from 'src/shared/components/ui';

import { useUpdateProduct } from '../hooks/useUpdateProduct';
import { useUpdateProductImage } from '../hooks/useUpdateProductImage';
import getLastActiveStageIndex from '../utils/getLastActiveStageIndex';

interface UpdateProductImageProps {
  product: ProductById | null;
}

const UpdateProductImage: React.FC<UpdateProductImageProps> = ({ product }) => {
  const { isOpen, onOpenChange } = useDisclosure();
  const productImageMutation = useUpdateProductImage();
  const productDetailMutation = useUpdateProduct();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onRemoveImage = () => {
    productDetailMutation.mutate(
      { image: '', id: product?.id as string },
      {
        onError: (error) => {
          addToast({
            color: 'danger',
            title: error?.message || 'Failed to remove product image.'
          });
        },
        onSuccess: () => {
          onOpenChange();
          addToast({
            color: 'success',
            title: 'Product image has been removed.'
          });
        }
      }
    );
  };

  const onChangeImage = (image: string) => {
    productDetailMutation.mutate(
      { image, id: product?.id as string },
      {
        onError: (error) => {
          addToast({
            color: 'danger',
            title: error?.message || 'Failed to update product image.'
          });
        },
        onSuccess: () => {
          addToast({
            color: 'success',
            title: 'Product image has been updated.'
          });
        }
      }
    );
  };

  const onUploadImage = (file: File) => {
    const currentStageIndex = getLastActiveStageIndex(
      product?.detail.lifecycle ?? []
    );

    const currentStageId = product?.detail.lifecycle[currentStageIndex].id;

    productImageMutation.mutate(
      { image: file, id: product?.id, currentStageId: currentStageId },
      {
        onError: (error) => {
          addToast({
            color: 'danger',
            title: error?.message || 'Failed to upload product image.'
          });
        },
        onSuccess: (data: any) => {
          onChangeImage(data[0].url);
        }
      }
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const isValidType = ['image/png', 'image/jpeg', 'image/jpg'].includes(
      file.type
    );

    if (!isValidType) {
      addToast({
        color: 'warning',
        title: 'Only PNG, JPEG, and JPG files are allowed.'
      });

      return;
    }

    onUploadImage(file);
  };

  return (
    <>
      <div className="flex max-w-fit relative group">
        <AppImage
          id={product?.id}
          className="size-10 object-cover min-w-10 rounded-xl bg-gray-2 border border-gray-4"
          src={product?.image ?? undefined}
        />

        {productImageMutation.isPending || productDetailMutation.isPending ? (
          <div className="flex flex-col absolute justify-center items-center z-10 inset-0 bg-black/30 rounded-xl">
            <Spinner color="white" />
          </div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              whileHover={{ opacity: 1 }}
              className="flex absolute items-center justify-center inset-0 p-4 gap-2 z-10 bg-black/30 rounded-xl"
            >
              <ButtonV2
                color="default"
                size="sm"
                onPress={() => fileInputRef.current?.click()}
                isIconOnly
                className="!rounded-lg"
              >
                <PencilIcon className="size-3" />
              </ButtonV2>
            </motion.div>
            {/*
            <motion.div
              onClick={onOpenChange}
              className="cursor-pointer group-hover:opacity-100 opacity-0 -right-2 -top-2 flex absolute size-4 items-center justify-center z-10 rounded-full bg-black text-white"
            >
              <XMarkIcon className="size-3" />
            </motion.div> */}
          </>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".png, .jpeg, .jpg"
        className="hidden"
        onChange={handleFileChange}
      />

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 p-5 border-b border-gray-4">
                Are you sure you want to remove this image?
              </ModalHeader>

              <ModalFooter className="justify-start border-t border-gray-4 p-5">
                <ButtonV2
                  size="sm"
                  color="default"
                  variant="flat"
                  onPress={() => onClose()}
                  isDisabled={productImageMutation.isPending}
                >
                  No
                </ButtonV2>
                <ButtonV2
                  onPress={onRemoveImage}
                  size="sm"
                  color="dark"
                  isLoading={productImageMutation.isPending}
                  isDisabled={productImageMutation.isPending}
                >
                  Yes
                </ButtonV2>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default UpdateProductImage;
